import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { BACKEND_URL } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Circle, ChevronLeft, ChevronRight, ShieldCheck, AlertTriangle, Send } from "lucide-react";
import Logo from "@/components/Logo";

const SECTIONS = [
  { id: "A", label: "Business Information" },
  { id: "B", label: "Services & Priorities" },
  { id: "C", label: "Communication Preferences" },
  { id: "D", label: "Tools & Platforms" },
  { id: "E", label: "Work Preferences" },
  { id: "F", label: "Documents" },
  { id: "G", label: "Final Confirmation" },
];
const COMM_METHODS = ["Email", "Phone", "Text", "Slack", "Microsoft Teams", "Other"];
const TOOLS = ["Google Workspace", "Google Calendar", "Microsoft 365", "Outlook", "Zoom", "Microsoft Teams", "Slack", "Canva", "QuickBooks", "CRM", "Project Management Software", "Cloud Storage", "Other"];

async function pfetch(path, opts = {}) {
  const res = await fetch(`${BACKEND_URL}/api${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
  });
  if (!res.ok) {
    let d = res.statusText; try { const j = await res.json(); d = j.detail || d; } catch (_) {}
    const err = new Error(typeof d === "string" ? d : JSON.stringify(d));
    err.payload = d;
    throw err;
  }
  return res.json();
}

const EMPTY = {
  business_info: { name: "", website: "", industry: "", description: "", time_zone: "", business_hours: "" },
  services_priorities: { services_being_provided: "", top_priorities: "", recurring_tasks: "", current_challenges: "", short_term_goals: "", long_term_goals: "", special_instructions: "" },
  communication_prefs: { methods: [], best_days: "", best_times: "", response_method: "", meeting_required: false, meeting_frequency: "", meeting_day: "", meeting_time: "" },
  tools_platforms: { tools: [], other_tools: "", additional_instructions: "" },
  work_preferences: { prioritization: "", communication_style: "", approval_process: "", approver: "", deadlines: "", additional: "" },
  final_confirmation: { additional_info: "", confirmed: false, signature: "", signed_at: "" },
};

export default function PortalOnboarding() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await pfetch(`/portal/${token}/onboarding`);
        setData(d);
        const onb = d.onboarding || {};
        setForm({
          business_info: { ...EMPTY.business_info, ...(onb.business_info || {}) },
          services_priorities: { ...EMPTY.services_priorities, ...(onb.services_priorities || {}) },
          communication_prefs: { ...EMPTY.communication_prefs, ...(onb.communication_prefs || {}) },
          tools_platforms: { ...EMPTY.tools_platforms, ...(onb.tools_platforms || {}) },
          work_preferences: { ...EMPTY.work_preferences, ...(onb.work_preferences || {}) },
          final_confirmation: { ...EMPTY.final_confirmation, ...(onb.final_confirmation || {}) },
        });
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const openChangeRequests = useMemo(() => {
    return (data?.onboarding?.change_requests || []).filter((c) => !c.resolved_at);
  }, [data]);

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const r = await pfetch(`/portal/${token}/onboarding`, {
        method: "PATCH", body: JSON.stringify(form),
      });
      setData((prev) => ({ ...prev, section_progress: r.section_progress, progress_percent: r.progress_percent, onboarding: { ...(prev?.onboarding || {}), status: r.status } }));
      if (!silent) toast.success("Progress saved — you can come back later");
    } catch (e) { if (!silent) toast.error(e.message); }
    finally { setSaving(false); }
  };

  const submit = async () => {
    // Persist first
    await save(true);
    setSubmitting(true);
    try {
      const r = await pfetch(`/portal/${token}/onboarding/submit`, { method: "POST" });
      toast.success("Onboarding submitted — thank you!");
      setData((prev) => ({ ...prev, onboarding: { ...(prev?.onboarding || {}), status: r.status }, progress_percent: r.progress_percent }));
      setStep(SECTIONS.length); // move past all sections to Thanks
    } catch (e) {
      const p = e.payload;
      if (p && typeof p === "object" && p.error === "missing_sections") {
        toast.error(p.message);
        const first = (p.sections || [])[0];
        if (first) setStep(SECTIONS.findIndex((s) => s.id === first));
      } else { toast.error(e.message); }
    } finally { setSubmitting(false); }
  };

  const toggleArr = (section, key, v) => setForm((f) => {
    const cur = f[section][key] || [];
    return { ...f, [section]: { ...f[section], [key]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] } };
  });

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] text-[#57534E]">Loading…</div>;
  if (error) return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-3xl border border-[#E8E2D9] p-8 text-center">
        <div className="w-14 h-14 rounded-2xl bg-[#FDDCDC] mx-auto flex items-center justify-center mb-4"><AlertTriangle size={22} className="text-[#B02222]" /></div>
        <h2 className="font-display font-bold text-xl">This onboarding link is not available</h2>
        <p className="text-sm text-[#57534E] mt-2">Please contact your provider for a fresh link.</p>
      </div>
    </div>
  );

  const status = data.onboarding?.status || "not_started";
  const progress = data.progress_percent || 0;
  const submitted = ["submitted", "resubmitted", "approved", "complete", "under_review"].includes(status);

  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-16" data-testid="portal-onboarding-page">
      <header className="bg-[#2C3A29] text-[#FAF8F5]">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Logo variant="full" size={36} onDark />
          <div className="text-xs text-[#A8A29E]">Client Onboarding</div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-8 space-y-6">
        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8">
          <div className="text-xs text-[#57534E] uppercase tracking-widest mb-2">{data.org_name}</div>
          <h1 className="font-display font-bold text-3xl tracking-tight mb-1">Welcome to ClientDesk VA</h1>
          <p className="text-sm text-[#57534E] mb-5">Please complete your onboarding information below. Your progress will be saved so you can return and finish later.</p>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm">
              <div className="text-xs text-[#57534E]">Onboarding progress</div>
              <div className="font-display font-bold text-2xl">{progress}%</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-[#57534E]">Status</div>
              <div className="text-sm font-semibold capitalize">{status.replace(/_/g, " ")}</div>
            </div>
          </div>
          <div className="h-2 rounded-full bg-[#F5EDE4] overflow-hidden mt-3">
            <div className="h-full rounded-full bg-[#E8712C] transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {openChangeRequests.length > 0 && (
          <div className="bg-[#FDE9D8] border border-[#F0C69A] rounded-3xl p-5" data-testid="portal-change-requests">
            <div className="flex items-center gap-2 text-[#C55A1F] font-semibold text-sm mb-2">
              <AlertTriangle size={16} /> Your provider requested changes
            </div>
            <ul className="space-y-1.5 text-sm">
              {openChangeRequests.map((cr, i) => (
                <li key={cr.id || `${cr.section}-${i}`}><b>Section {cr.section}:</b> {cr.note}</li>
              ))}
            </ul>
            <p className="text-xs text-[#57534E] mt-2">Please update the relevant sections and resubmit below.</p>
          </div>
        )}

        {submitted && step >= SECTIONS.length ? (
          <div className="bg-white rounded-3xl border border-[#E8E2D9] p-8 text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#E8F0E4] mx-auto flex items-center justify-center mb-3"><CheckCircle2 size={22} className="text-[#2C3A29]" /></div>
            <h2 className="font-display font-bold text-2xl mb-1">Thank you</h2>
            <p className="text-sm text-[#57534E]">Your onboarding information has been submitted successfully. Your ClientDesk VA provider will review your information.</p>
          </div>
        ) : (
          <>
            {/* Stepper */}
            <ol className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {SECTIONS.map((s, i) => {
                const done = data.section_progress?.[s.id];
                const active = i === step;
                return (
                  <li key={s.id}>
                    <button onClick={() => setStep(i)} className={`w-full text-left rounded-xl border p-3 transition-all ${
                      active ? "bg-white border-[#E8712C] shadow-sm" : done ? "bg-[#E8F0E4] border-[#D3E2CD]" : "bg-white border-[#E8E2D9]"
                    }`} data-testid={`portal-onb-step-${s.id}`}>
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#57534E]">
                        {done ? <CheckCircle2 size={12} className="text-[#2C3A29]" /> : <Circle size={12} />}
                        Section {s.id}
                      </div>
                      <div className={`mt-0.5 text-sm font-semibold ${active ? "text-[#1C1917]" : "text-[#57534E]"}`}>{s.label}</div>
                    </button>
                  </li>
                );
              })}
            </ol>

            <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8">
              {step === 0 && <SectionA value={form.business_info} onChange={(v) => setForm({ ...form, business_info: v })} />}
              {step === 1 && <SectionB value={form.services_priorities} onChange={(v) => setForm({ ...form, services_priorities: v })} />}
              {step === 2 && <SectionC value={form.communication_prefs} onChange={(v) => setForm({ ...form, communication_prefs: v })} onToggle={(k, v) => toggleArr("communication_prefs", k, v)} />}
              {step === 3 && <SectionD value={form.tools_platforms} onChange={(v) => setForm({ ...form, tools_platforms: v })} onToggle={(k, v) => toggleArr("tools_platforms", k, v)} />}
              {step === 4 && <SectionE value={form.work_preferences} onChange={(v) => setForm({ ...form, work_preferences: v })} />}
              {step === 5 && <SectionF />}
              {step === 6 && <SectionG value={form.final_confirmation} onChange={(v) => setForm({ ...form, final_confirmation: v })} client={data.client} />}

              <div className="mt-6 pt-5 border-t border-[#E8E2D9] flex flex-wrap items-center justify-between gap-2">
                <Button variant="outline" className="rounded-full" disabled={step === 0}
                  onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="portal-onb-back">
                  <ChevronLeft size={14} className="mr-1" /> Previous
                </Button>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" className="rounded-full" onClick={() => save(false)} disabled={saving}
                    data-testid="portal-onb-save">
                    {saving ? "Saving…" : "Save & continue later"}
                  </Button>
                  {step < SECTIONS.length - 1 ? (
                    <Button onClick={async () => { await save(true); setStep((s) => s + 1); }}
                      className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]" data-testid="portal-onb-next">
                      Next <ChevronRight size={14} className="ml-1" />
                    </Button>
                  ) : (
                    <Button onClick={submit} disabled={submitting}
                      className="rounded-full bg-[#2C3A29] hover:bg-[#3E4C38] text-white" data-testid="portal-onb-submit">
                      <Send size={14} className="mr-1.5" /> {submitting ? "Submitting…" : "Submit onboarding"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        <p className="text-center text-xs text-[#A8A29E] py-4">Powered by ClientDesk · This link is private to you.</p>
      </div>
    </div>
  );
}

/* --- Sections --- */
function StepHeading({ letter, title, subtitle }) {
  return (
    <div className="mb-4">
      <div className="text-xs uppercase tracking-widest text-[#57534E]">Section {letter}</div>
      <h2 className="font-display font-bold text-2xl tracking-tight">{title}</h2>
      {subtitle && <p className="text-sm text-[#57534E]">{subtitle}</p>}
    </div>
  );
}
function Field({ label, required, wide, children }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label className="text-sm">{label}{required && <span className="text-[#E8712C]"> *</span>}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function SectionA({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div>
      <StepHeading letter="A" title="Business Information" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Business/Organization name" required><Input data-testid="pob-name" value={value.name} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Website"><Input value={value.website} onChange={(e) => set("website", e.target.value)} /></Field>
        <Field label="Industry"><Input value={value.industry} onChange={(e) => set("industry", e.target.value)} /></Field>
        <Field label="Time zone" required><Input placeholder="e.g. America/New_York" data-testid="pob-tz" value={value.time_zone} onChange={(e) => set("time_zone", e.target.value)} /></Field>
        <Field label="Business description" wide><Textarea rows={2} value={value.description} onChange={(e) => set("description", e.target.value)} /></Field>
        <Field label="Business hours" wide><Input placeholder="e.g. Mon-Fri 9am-5pm" value={value.business_hours} onChange={(e) => set("business_hours", e.target.value)} /></Field>
      </div>
    </div>
  );
}
function SectionB({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div>
      <StepHeading letter="B" title="Services & Priorities" />
      {[
        ["services_being_provided", "Services being provided", true],
        ["top_priorities", "Top priorities"],
        ["recurring_tasks", "Important recurring tasks"],
        ["current_challenges", "Current challenges"],
        ["short_term_goals", "Short-term goals"],
        ["long_term_goals", "Long-term goals"],
        ["special_instructions", "Special instructions"],
      ].map(([k, l, req]) => (
        <Field key={k} label={l} required={!!req}>
          <Textarea rows={2} data-testid={`pob-b-${k}`} value={value[k]} onChange={(e) => set(k, e.target.value)} />
        </Field>
      ))}
    </div>
  );
}
function SectionC({ value, onChange, onToggle }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div>
      <StepHeading letter="C" title="Communication Preferences" />
      <Field label="Preferred communication method" required>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
          {COMM_METHODS.map((m) => (
            <label key={m} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${(value.methods || []).includes(m) ? "bg-[#FDE9D8] border-[#F0C69A]" : "bg-white border-[#E8E2D9]"}`}>
              <input type="checkbox" checked={(value.methods || []).includes(m)} onChange={() => onToggle("methods", m)} className="accent-[#E8712C]" data-testid={`pob-c-${m.toLowerCase()}`} />{m}
            </label>
          ))}
        </div>
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <Field label="Best days to contact"><Input value={value.best_days} onChange={(e) => set("best_days", e.target.value)} /></Field>
        <Field label="Best times to contact"><Input value={value.best_times} onChange={(e) => set("best_times", e.target.value)} /></Field>
        <Field label="Preferred response method"><Input value={value.response_method} onChange={(e) => set("response_method", e.target.value)} /></Field>
        <Field label="Regular meeting required?">
          <Select value={value.meeting_required ? "yes" : "no"} onValueChange={(v) => set("meeting_required", v === "yes")}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes</SelectItem></SelectContent>
          </Select>
        </Field>
      </div>
      {value.meeting_required && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <Field label="Meeting frequency"><Input value={value.meeting_frequency} onChange={(e) => set("meeting_frequency", e.target.value)} /></Field>
          <Field label="Preferred meeting day"><Input value={value.meeting_day} onChange={(e) => set("meeting_day", e.target.value)} /></Field>
          <Field label="Preferred meeting time"><Input value={value.meeting_time} onChange={(e) => set("meeting_time", e.target.value)} /></Field>
        </div>
      )}
    </div>
  );
}
function SectionD({ value, onChange, onToggle }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div>
      <StepHeading letter="D" title="Tools & Platforms" subtitle="Select the tools your team uses. Never enter passwords, API keys, or security codes." />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
        {TOOLS.map((t) => (
          <label key={t} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${(value.tools || []).includes(t) ? "bg-[#FDE9D8] border-[#F0C69A]" : "bg-white border-[#E8E2D9]"}`}>
            <input type="checkbox" checked={(value.tools || []).includes(t)} onChange={() => onToggle("tools", t)} className="accent-[#E8712C]" />{t}
          </label>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <Field label="Other tools"><Input value={value.other_tools} onChange={(e) => set("other_tools", e.target.value)} /></Field>
        <Field label="Additional instructions"><Input value={value.additional_instructions} onChange={(e) => set("additional_instructions", e.target.value)} /></Field>
      </div>
      <p className="text-[11px] text-[#B02222] mt-3">Never enter passwords, API keys, or security codes here.</p>
    </div>
  );
}
function SectionE({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div>
      <StepHeading letter="E" title="Work Preferences" />
      {[
        ["prioritization", "How should tasks be prioritized?", true],
        ["communication_style", "Preferred communication style"],
        ["approval_process", "Approval process"],
        ["approver", "Who approves completed work?"],
        ["deadlines", "Important deadlines"],
        ["additional", "Additional work preferences"],
      ].map(([k, l, req]) => (
        <Field key={k} label={l} required={!!req}>
          <Textarea rows={2} value={value[k]} onChange={(e) => set(k, e.target.value)} />
        </Field>
      ))}
    </div>
  );
}
function SectionF() {
  return (
    <div>
      <StepHeading letter="F" title="Documents" subtitle="Upload SOPs, brand guidelines, or reference files from the main portal Documents section — they'll be linked to your record automatically." />
      <div className="bg-[#FAF8F5] border border-[#E8E2D9] rounded-2xl p-5 text-sm text-[#57534E]">
        This section is optional. When you upload documents from the main portal page, they will be linked to your onboarding automatically. You can proceed to the next step.
      </div>
    </div>
  );
}
function SectionG({ value, onChange, client }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  useEffect(() => {
    if (!value.signature && client) {
      onChange({ ...value, signature: `${client.first_name || ""} ${client.last_name || ""}`.trim() });
    }
    // eslint-disable-next-line
  }, [client?.first_name, client?.last_name]);
  const today = new Date().toLocaleDateString();
  return (
    <div>
      <StepHeading letter="G" title="Final Confirmation" />
      <Field label="Additional information"><Textarea rows={2} value={value.additional_info} onChange={(e) => set("additional_info", e.target.value)} /></Field>
      <label className="mt-3 flex items-start gap-3 p-3 rounded-xl border border-[#E8E2D9] bg-white cursor-pointer">
        <input type="checkbox" checked={!!value.confirmed} onChange={(e) => set("confirmed", e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#E8712C]" data-testid="pob-g-confirm" />
        <span className="text-sm">I confirm the information provided in this onboarding form is accurate and complete.</span>
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <Field label="Electronic signature (full name)" required><Input data-testid="pob-g-sig" value={value.signature} onChange={(e) => set("signature", e.target.value)} /></Field>
        <Field label="Date signed"><Input readOnly value={value.signed_at ? new Date(value.signed_at).toLocaleDateString() : today} className="bg-[#FAF8F5]" /></Field>
      </div>
    </div>
  );
}
